import { useEffect, useMemo, useState } from 'react';

import { TableColumnsType } from 'antd';
import classNames from 'classnames';
import { getShortName, UserAvatar } from 'components-antd';
import moment from 'moment';
import {
  requestGetCampaignMembersByCampaignIdEffect,
  requestUpdateCampaignEffect,
} from 'store/effects/radarKits';
import tableStyles from '../../../components/Table/styles.module.scss';
import { DeleteIcon } from '../../components';
import { DropdownMenu } from '../../components/DropdownMenu';
import parentStyles from '../../styles.module.scss';
import styles from '../styles.module.scss';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

type EnrolledDataType = {
  user: {
    name: string;
    picture?: string;
    id: string;
  };
  dateAdded: string;
  sent: number;
  opened: number;
  tasksCompleted: number;
};

type UseAssignedClientProps = {
  onMemberUpdate?: () => void;
};

export const useAssignedClient = (props?: UseAssignedClientProps) => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();

  const [selectedMembers, setSelectedMembers] = useState<{ ClientId: number; AddressId: number }[]>(
    [],
  );
  const [campaignMembers, setCampaignMembers] = useState<any[]>([]);
  const [enrolledClientsLoading, setEnrolledClientsLoading] = useState(true);
  const [isAssignClientsModalOpen, setIsAssignClientsModalOpen] = useState<boolean>(false);

  useEffect(() => {
    setEnrolledClientsLoading(true);
    dispatch(
      requestGetCampaignMembersByCampaignIdEffect(id, {}, (err, res) => {
        if (!err) {
          const members = res?.data?.result || [];
          setCampaignMembers(members);
          setSelectedMembers(
            members.flatMap((client) =>
              client.member.Addresses.map((address) => ({
                ClientId: client.member.ClientId,
                AddressId: address.AddressId,
              })),
            ),
          );
          setEnrolledClientsLoading(false);
        }
      }),
    );
  }, [id, dispatch]);

  const enrolledClientsDataSource = useMemo<EnrolledDataType[]>(() => {
    if (campaignMembers?.length === 0) return [];
    return campaignMembers.map((cm) => ({
      user: {
        name: cm.member.Client.User.FirstName + ' ' + cm.member.Client.User.LastName,
        picture: cm.member.Client.User.AvatarUrl,
        id: cm.member.ClientId,
      },
      dateAdded: moment(cm.member.CreatedDate).format('MM/DD/YYYY'),
      sent: cm.sentCount,
      opened: cm.openedCount,
      tasksCompleted: cm.taskCompleted,
    }));
  }, [campaignMembers]);

  const enrolledColumnsInternal = (dispatch): TableColumnsType<EnrolledDataType> => [
    {
      title: 'Name',
      dataIndex: 'user',
      key: 'user',
      className: tableStyles.tableCol,
      sorter: (a, b) => a.user.name.localeCompare(b.user.name),
      render: (user: any) => (
        <div className={parentStyles.tableColNameWrapper}>
          <div className={styles.iconWrapper}>
            <UserAvatar
              size={32}
              src={user?.picture}
              name={user.name}
              minimizeAvatar
              placeholder={getShortName(user.name)}
            />
          </div>
          <div className={classNames(styles.detailsWrapper, styles.name)}>{user.name}</div>
        </div>
      ),
    },
    {
      title: 'Added',
      dataIndex: 'dateAdded',
      key: 'dateAdded',
      className: tableStyles.tableCol,
      sorter: (a, b) => moment(a.dateAdded).unix() - moment(b.dateAdded).unix(),
      render: (value: string) => <div className={tableStyles.tableNormalCell}>{value}</div>,
    },
    {
      title: 'Sent',
      dataIndex: 'sent',
      key: 'sent',
      className: tableStyles.tableCol,
      sorter: (a, b) => a.sent - b.sent,
      render: (value: string) => <div className={tableStyles.tableNormalCell}>{value}</div>,
    },
    {
      title: 'Opened',
      dataIndex: 'opened',
      key: 'opened',
      className: tableStyles.tableCol,
      sorter: (a, b) => a.opened - b.opened,
      render: (value: string) => <div className={tableStyles.tableNormalCell}>{value}</div>,
    },
    {
      title: 'Tasks Completed',
      dataIndex: 'tasksCompleted',
      key: 'tasksCompleted',
      className: tableStyles.tableCol,
      sorter: (a, b) => a.tasksCompleted - b.tasksCompleted,
      render: (value: string) => <div className={tableStyles.tableNormalCell}>{value}</div>,
    },
    {
      title: '',
      key: 'action',
      className: tableStyles.tableCol,
      width: 30,
      render: (value, record) => (
        <div className={tableStyles.tableActionCell}>
          <DropdownMenu
            id={record.user.id}
            options={[
              {
                label: 'Remove',
                icon: <DeleteIcon />,
                onSelect: (memberId) => {
                  dispatch(
                    requestUpdateCampaignEffect(
                      {
                        Id: Number(id),
                        CampaignMembers: selectedMembers.filter(
                          (member) => member.ClientId !== Number(memberId),
                        ),
                      },
                      {},
                      (err, res) => {
                        if (!err && res.data.result.Id) {
                          dispatch(
                            requestGetCampaignMembersByCampaignIdEffect(
                              id,
                              { silent: true },
                              (err, res) => {
                                if (!err) {
                                  const members = res?.data?.result || [];
                                  setCampaignMembers(members);
                                  setSelectedMembers(
                                    members.flatMap((client) =>
                                      client.member.Addresses.map((address) => ({
                                        ClientId: client.member.ClientId,
                                        AddressId: address.AddressId,
                                      })),
                                    ),
                                  );
                                  props?.onMemberUpdate?.();
                                }
                              },
                            ),
                          );
                        }
                        setIsAssignClientsModalOpen(false);
                      },
                    ),
                  );
                },
              },
            ]}
          />
        </div>
      ),
    },
  ];

  const enrolledColumns = useMemo(
    () => enrolledColumnsInternal(dispatch),
    [campaignMembers, selectedMembers],
  );

  return {
    enrolledColumns,
    selectedMembers,
    setSelectedMembers,
    campaignMembers,
    setCampaignMembers,
    enrolledClientsLoading,
    isAssignClientsModalOpen,
    enrolledClientsDataSource,
    setIsAssignClientsModalOpen,
  };
};
